import React from 'react';
import { Provider } from 'react-redux';
import reduxSaga from 'redux-saga';
import { rootSaga } from '../sagas';
import { SerializedError, ThunkDispatch, UnknownAction, configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import type {
    ApiEndpointMutation,
    EndpointDefinitions,
    FetchBaseQueryError,
    MutationDefinition,
    QueryArgFrom,
    ResultTypeFrom,
} from '@reduxjs/toolkit/query';
import { parseApiErrorMessage } from '../apiHooks/utils';
import { accountsReducer } from './accounts/accountsReducer';
import accountSettingsSlice from '../features/accountSettings/accountSettingsSlice';
import appCreateSlice from '../features/appCreate/slice';
import appPageSlice from '../features/appPage/appPageSlice';
import appRunSlice from '../features/appRun/appRunSlice';
import { notificationReducer } from './notification/notificationReducer';
import { userReducer } from './user/userReducer';
import userFlowSlice from '../features/userFlow/userFlowSlice';
import { baseApi } from '../apiHooks/baseApi';
import authSlice from '../apiHooks/auth/stateSlice';

const sagaMiddleware = reduxSaga();

export const store = configureStore({
    reducer: {
        auth: authSlice.reducer,
        [baseApi.reducerPath]: baseApi.reducer,

        accounts: accountsReducer,
        accountSettings: accountSettingsSlice.reducer,
        appCreate: appCreateSlice.reducer,
        appPage: appPageSlice.reducer,
        appRun: appRunSlice.reducer,
        notification: notificationReducer,
        user: userReducer,
        userFlow: userFlowSlice.reducer,
    },
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware()
            // Adding the api middleware enables caching, invalidation, polling, and other useful features of `rtk-query`.
            .concat(baseApi.middleware)
            .concat(sagaMiddleware),
});

// Required for refetchOnFocus/refetchOnReconnect behaviors
setupListeners(store.dispatch);

sagaMiddleware.run(rootSaga);

export function WrapRootElementWithReduxProvider(props: { element: any }) {
    return <Provider store={store}>{props.element}</Provider>;
}

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const dispatch: ThunkDispatch<RootState, undefined, UnknownAction> = (action) =>
    (store.dispatch as ThunkDispatch<RootState, undefined, UnknownAction>)(action);

export async function dispatchMutation<
    Definition extends MutationDefinition<any, any, any, any>,
    Definitions extends EndpointDefinitions,
>(
    endpoint: ApiEndpointMutation<Definition, Definitions>,
    arg: QueryArgFrom<Definition>,
): Promise<ResultTypeFrom<Definition>> {
    const dispatchResult = dispatch(endpoint.initiate(arg));
    try {
        return await dispatchResult.unwrap();
    } catch (error) {
        throw new Error(parseApiErrorMessage(error as FetchBaseQueryError | SerializedError));
    } finally {
        dispatchResult.reset();
    }
}

export async function dispatchQuery<ResultType, QueryArg>(
    endpoint: { initiate: (arg: QueryArg, options?: { forceRefetch?: boolean }) => any },
    arg: QueryArg,
    options?: { forceRefetch?: boolean },
): Promise<ResultType> {
    const dispatchResult = dispatch(
        endpoint.initiate(arg, options?.forceRefetch ? { forceRefetch: true } : undefined),
    );
    try {
        return await dispatchResult.unwrap();
    } finally {
        dispatchResult.unsubscribe();
    }
}

export * from './common.types';
export * from './utilityFunctions';
